import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  makeStyles,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { useEffect, useMemo, useState } from 'react';
import { CompanyReputation, BadgeApiItem } from '../../types';
import { parseBadgeRanges, getBadgeByScore } from './BadgeConfig';
import { badgeStyleMap } from '../GeneralBadgeTab/GeneralBadgeTab';
import { updateCompanyReputation } from '@api/company-reputation';

interface Props {
  company: CompanyReputation;
  computedScore: number;
  overrideScore: number | null;
  displayScore: number;
  badges: BadgeApiItem[];
}
const useStyles = makeStyles({
  greenCheckbox: {
    color: '#2E7D32 !important',
  },
    disabledCheckbox: {
    color: '#BDBDBD !important', // Grey color when disabled
    cursor: 'not-allowed'
  },
});
export const ScoreCard: React.FC<Props> = ({
  company,
  computedScore,
  overrideScore,
  displayScore,
  badges,
}) => {
  const classes = useStyles();
  const parsedBadges = useMemo(
    () => parseBadgeRanges(badges),
    [badges]
  );

  /*   * LOCAL STATES   */

  const [localOverrideScore, setLocalOverrideScore] =   useState<number | null>(overrideScore);
  const [localDisplayScore, setLocalDisplayScore] =  useState<number>(displayScore);
  const [previousScore, setPreviousScore] =  useState<number | null>(null);
  
  /*   * DIALOG STATES   */

  const [open, setOpen] = useState(false);
  const [newScore, setNewScore] =    useState<number>(displayScore);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [useComputedScore, setUseComputedScore] = useState(localOverrideScore === null);
  const disableUseComputedScore =  localOverrideScore === null ||   Math.abs(localOverrideScore - computedScore) < 0.001;
  /*   * SYNC WITH API RESPONSE   */
  useEffect(() => {
    setPreviousScore(null);
    setLocalOverrideScore(overrideScore);
    setLocalDisplayScore(displayScore);
    setPreviousScore(null);
    setReason('');
    setUseComputedScore(false);
  }, [company.vtr_id]);

  useEffect(() => {
    setLocalOverrideScore(overrideScore);
    setLocalDisplayScore(displayScore);
  }, [overrideScore, displayScore]);

  /*   * VALIDATION   */

  const isInvalidScore =
    newScore < 0 || newScore > 5;

  /*   * BADGES   */

  const finalBadge = useMemo(
    () =>
      getBadgeByScore(
        localDisplayScore,
        parsedBadges
      ),
    [localDisplayScore, parsedBadges]
  );

  const computedBadge = useMemo(
    () =>
      getBadgeByScore(
        computedScore,
        parsedBadges
      ),
    [computedScore, parsedBadges]
  );

  const hasValidOverride =
    localOverrideScore !== null &&
    localOverrideScore !== computedScore;

  const overrideBadge = useMemo(
    () =>
      hasValidOverride
        ? getBadgeByScore(
            localOverrideScore,
            parsedBadges
          )
        : null,
    [hasValidOverride, localOverrideScore, parsedBadges]
  );

  const overrideDisplayValue =
    localOverrideScore === null ||
    localOverrideScore === computedScore
      ? 'N/A'
      :  Number(localOverrideScore).toFixed(2);

  const handleOpen = () => {
    setNewScore(localDisplayScore);
    setReason('');
    setUseComputedScore(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = () => {
    setSaving(true);

    updateCompanyReputation(company.vtr_id, {
      override_reputation_score: useComputedScore
        ? null
        : newScore,
      override_reputation_score_reason: reason,
    }).subscribe({
      next: () => {
        setPreviousScore(localDisplayScore);

        if (useComputedScore) {
          setLocalOverrideScore(null);
          setLocalDisplayScore(computedScore);
        } else {
          setLocalOverrideScore(newScore);
          setLocalDisplayScore(newScore);
        }

        setOpen(false);
        setSaving(false);
      },

      error: (err) => {
        console.error(
          'Failed to update reputation',
          err
        );

        setSaving(false);
      },
    });
  };

  return (
    <>
      <Card
        elevation={3}
        style={{
          background: 'linear-gradient(135deg, #EEF2FF, #FFFFFF)',
          borderLeft: '6px solid #3F51B5',
        }}>
        <CardContent>
          {/* COMPANY NAME */}

          <Typography variant="h5" gutterBottom>
            {company.name}
          </Typography>

          {/* COMPANY ID */}

          <Box mb={1}>
            <Chip label={`ID: ${company.vtr_id}`} size="small" color="primary" />
          </Box>

          {/* STATUS CHIPS */}

          <Box style={{ marginBottom: 12 }}>
            {company.company_status && (
              <Chip
                label={company.company_status}
                size="small"
                style={{
                  marginRight: 10,
                  backgroundColor: '#f29e9e',
                }}
              />
            )}

            {company.company_active && (
              <Chip
                label={company.company_active}
                size="small"
                style={{
                  marginRight: 10,
                  backgroundColor: '#4ebeb4',
                }}
              />
            )}
          </Box>

          {/* FINAL SCORE */}

          <Typography variant="overline" color="secondary">
            Final Reputation Score
          </Typography>

          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Typography
              variant="h2"
              color="primary"
              style={{
                fontWeight: 700,
              }}>
              {Number(localDisplayScore).toFixed(2)}
            </Typography>

            {finalBadge && (
              <Tooltip title={`Range ${finalBadge.range.replace('TO', ' – ')}`}>
                <Chip
                  label={finalBadge.name}
                  style={{
                    fontWeight: 700,
                    fontSize: 16,
                    backgroundColor: badgeStyleMap[finalBadge.name].bg,
                    color: badgeStyleMap[finalBadge.name].color,
                  }}
                />
              </Tooltip>
            )}

            <IconButton size="small" onClick={handleOpen}>
              <EditIcon />
            </IconButton>
          </Box>

          {/* MANUAL OVERRIDE LABEL */}
          {/* 
          {localOverrideScore !== null && (
            <Box mt={1}>
              <Chip
                label="MANUAL OVERRIDE"
                size="small"
                style={{
                  background: '#FFF3CD',
                  color: '#856404',
                  fontWeight: 700,
                }}
              />
            </Box>
          )} */}

          {/* COMPUTED SCORE */}

          <Box mt={3}>
            <Typography variant="subtitle2" color="textSecondary">
              Computed Reputation Score
            </Typography>

            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography variant="body1">{computedScore.toFixed(2)}</Typography>

              {computedBadge && (
                <Chip
                  label={computedBadge.name}
                  size="small"
                  style={{
                    backgroundColor: badgeStyleMap[computedBadge.name].bg,
                    color: badgeStyleMap[computedBadge.name].color,
                  }}
                />
              )}
            </Box>
          </Box>

          {/* OVERRIDE SCORE */}

          <Box mt={2}>
            <Typography variant="subtitle2" color="textSecondary">
              Editorial Reputation Score
            </Typography>

            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography variant="body1">{overrideDisplayValue}</Typography>

              {overrideBadge && (
                <Chip
                  label={overrideBadge.name}
                  size="small"
                  style={{
                    backgroundColor: badgeStyleMap[overrideBadge.name].bg,
                    color: badgeStyleMap[overrideBadge.name].color,
                  }}
                />
              )}
            </Box>
          </Box>

          {/* PREVIOUS SCORE */}

          {previousScore !== null && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="textSecondary">
                Previous Score
              </Typography>

              <Typography
                variant="body2"
                style={{
                  textDecoration: 'line-through',
                  opacity: 0.7,
                }}>
                {previousScore.toFixed(2)}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* EDIT DIALOG */}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Reputation Score</DialogTitle>

        <DialogContent>
          <TextField label="Company" fullWidth margin="dense" value={company.name} disabled />

          <TextField
            label="Current Final Score"
            fullWidth
            margin="dense"
            value={localDisplayScore}
            disabled
          />
          <FormControlLabel
            disabled={disableUseComputedScore}
            control={
              <Tooltip
                title={
                  disableUseComputedScore
                    ? 'No manually edited score to clear.'
                    : 'Clear the manually edited score and use the system-computed reputation score.'
                }
                arrow>
                <Checkbox
                  checked={useComputedScore}
                  disabled={disableUseComputedScore}
                  onChange={e => setUseComputedScore(e.target.checked)}
                  style={{
                    color: '#2E7D32',
                  }}
                  classes={{
                    checked: classes.greenCheckbox,
                    disabled: classes.disabledCheckbox,
                  }}
                />
              </Tooltip>
            }
            label={
              <Tooltip
                title={
                  disableUseComputedScore
                    ? 'Computed score is already being used.'
                    : 'When enabled, the editorial  score will be removed and the computed score will be used as the final reputation score.'
                }
                arrow>
                <Chip
                  label="Use Computed Score"
                  size="small"
                   style={{
                    backgroundColor: disableUseComputedScore
                      ? '#F5F5F5'
                      : '#E8F5E9',
                    color: disableUseComputedScore
                      ? '#9E9E9E'
                      : '#2E7D32',
                    fontWeight: 600,
                    cursor: disableUseComputedScore
                      ? 'not-allowed'
                      : 'help',
                  }}
                />
              </Tooltip>
            }
          />
          <TextField
            label="New Score"
            type="number"
            fullWidth
            margin="dense"
            disabled={useComputedScore}
            value={newScore}
            inputProps={{
              min: 0,
              max: 5,
              step: 0.1,
            }}
            helperText={ "Enter a score between 0 and 5."  }
            onChange={e => {
              let value = Number(e.target.value);

              if (value < 0) value = 0;

              if (value > 5) value = 5;

              setNewScore(value);
            }}
          />

          <TextField
            label="Reason"
            fullWidth
            margin="dense"
            multiline
            rows={3}
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>

          <Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={!reason.trim() || saving || (!useComputedScore && isInvalidScore)}>
            {saving ? 'Saving...' : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};